// Gargantua — supermassive black hole with a volumetric accretion disk
// and Schwarzschild light deflection. Visually inspired by the
// Interstellar (2014) hero shot: a wide warm-coloured disk lensed
// above and below the silhouette by gravitational deflection.
//
// Architecture parallels src/blackhole.js (stellar-mass, disk-less):
// renders inside a BackSide sphere centered on the BH world position
// so the raymarcher runs for every screen pixel inside the sphere and
// composites onto the underlying scene via blending. The underlying
// skybox panorama is sampled for lensed background light.
//
// The fragment shader is a direct port of complexthree.js BlackHole.js
// with two changes:
//   1. The synthetic starfield/nebula is replaced by a sample of the
//      project's existing equirectangular skybox texture, so the
//      lensed background reads as the same Milky Way panorama used
//      across the rest of the scene rather than a competing pattern.
//   2. World-space camera localization (cameraPosition - uBhWorldPos
//      scaled by 1/uRsWorld) so the raymarch can run in r_s units
//      while the mesh lives anywhere in the scene.

import * as THREE from 'three';
import { placeholderSkyCube } from './sky/skyCube.js';

// Steps per ray: the 2D view's, and a headset's (a Quest 3 at 72 Hz).
const MARCH_STEPS_2D = 180;
const XR_MARCH_STEPS = 96;

const GARGANTUA_CONSTANTS = {
  massSolar: 100_000_000,             // ~10^8 M☉, Interstellar canon
  spinParameter: 0.999,               // a/M, near-extremal Kerr
};

// --- Baked tileable FBM noise --------------------------------------
// The disk shader used to evaluate 4–6 octave value-noise FBM
// analytically for every volumetric sample: 2× fbm6 + 3× fbm + 1×
// ridge = 28 vnoise = ~112 sin-hash calls per sample, with up to 8
// sub-samples per marching step. That was the dominant per-pixel cost
// of the whole system. The same fractal statistics are baked once
// into a 512² texture (R = 4-octave FBM, G = 6-octave FBM) built on a
// wrapping lattice so it tiles seamlessly; each in-shader FBM
// evaluation becomes a single texture fetch. Filtering stays Linear
// without mips — the analytic version had no filtering either, so the
// aliasing character at distance is unchanged.
const NOISE_TEX_SIZE = 512;
const NOISE_PERIOD = 16;   // noise-space units per texture repeat; must match shader

let _noiseTexCache = null;

function latticeHash(ix, iy, oct) {
  let h = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(oct + 1, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function buildFbmTexture() {
  if (_noiseTexCache) return _noiseTexCache;
  const size = NOISE_TEX_SIZE;
  const v4 = new Float32Array(size * size);
  const v6 = new Float32Array(size * size);

  let amp = 0.5;
  for (let o = 0; o < 6; o++) {
    // Octave o: frequency 2^o, lattice wraps at NOISE_PERIOD·2^o so
    // every octave tiles across the texture edge.
    const wrap = NOISE_PERIOD << o;
    const grid = new Float32Array(wrap * wrap);
    for (let gy = 0; gy < wrap; gy++) {
      for (let gx = 0; gx < wrap; gx++) {
        grid[gy * wrap + gx] = latticeHash(gx, gy, o);
      }
    }
    const cellsPerTexel = wrap / size;
    for (let y = 0; y < size; y++) {
      const fy = y * cellsPerTexel;
      const iy = Math.floor(fy);
      const ty = fy - iy;
      const uy = ty * ty * (3 - 2 * ty);
      const iy1 = (iy + 1) % wrap;
      for (let x = 0; x < size; x++) {
        const fx = x * cellsPerTexel;
        const ix = Math.floor(fx);
        const tx = fx - ix;
        const ux = tx * tx * (3 - 2 * tx);
        const ix1 = (ix + 1) % wrap;
        const a = grid[iy * wrap + ix];
        const b = grid[iy * wrap + ix1];
        const c = grid[iy1 * wrap + ix];
        const d = grid[iy1 * wrap + ix1];
        const n = (a * (1 - ux) + b * ux) * (1 - uy) + (c * (1 - ux) + d * ux) * uy;
        const idx = y * size + x;
        if (o < 4) v4[idx] += amp * n;
        v6[idx] += amp * n;
      }
    }
    amp *= 0.5;
  }

  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    data[i * 4]     = Math.min(255, Math.round(v4[i] * 255));
    data[i * 4 + 1] = Math.min(255, Math.round(v6[i] * 255));
    data[i * 4 + 2] = 0;
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  _noiseTexCache = tex;
  return tex;
}

const VERTEX_SHADER = /* glsl */`
  varying vec3 vWorldPos;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

// --- Reduced-resolution march --------------------------------------
// The march is most of a frame wherever the hole fills the screen (about
// 16 ms at 2880 x 1800 on an M4 Max for the screensaver's hero framing).
// With a march scale below 1 the march runs into its own smaller target
// (GARG_REDUCED, twice the disc sub-samples, so its jitter grain stays as
// fine as today's once upscaled) and the mesh draws a composite of it at
// full resolution (GARG_COMPOSITE, the same shader):
//   - The shadow's edge is decided per full-resolution pixel. It is b =
//     B_CRIT exactly, the march's own test, and b, the ray's impact
//     parameter, is analytic for any pixel, so each pixel blends only march
//     texels from its own side: the edge is as sharp as a full-resolution
//     march, and inside it the disc in front of the hole still shows.
//   - Within RING_BAND of the edge (the lensed-sky ring and the disc's
//     images hugging the shadow, about 2% of the pixels at the hero
//     framing) the composite runs the full march itself, so the photon
//     ring is exactly the full-resolution render. It cross-fades into the
//     upsampled march over the band's outer 30%.
//   - Elsewhere the march's texels hold what each ray found rather than a
//     colour: the disc's emission and transmittance, and the escaped ray's
//     direction. The composite blends the four nearest bilinearly (leaving
//     out the shadow's; if all four are, it steps one texel outward along
//     b's gradient), then samples the sky with the blended direction and
//     applies the photon-ring lift and alpha from its own b, so lensed
//     stars stay a pixel wide. The disc is soft at a texel's scale.
// setMarchScale(1) is the full-resolution path, unchanged.
const FRAGMENT_SHADER = /* glsl */`
  precision highp float;

  varying vec3 vWorldPos;

  uniform vec3  uBhWorldPos;
  uniform float uRsWorld;          // scene-units per Schwarzschild radius
  uniform float uTime;
  uniform float uDiskInner;        // r_s units
  uniform float uDiskOuter;        // r_s units
  uniform float uExposure;
  uniform float uDustOpacity;
  uniform float uDustEmission;
  uniform float uOpacity;
  uniform samplerCube uSkybox;  // the sky as drawn (src/sky/skyCube.js)
  uniform float uHasSkybox;        // 0 → use procedural fallback
  uniform sampler2D uNoiseTex;     // baked tileable FBM (R=4-oct, G=6-oct)

  const float RS         = 1.0;
  const int   MAX_STEPS  = 180;
  uniform float uStepLimit; // MAX_STEPS in 2D; a headset marches fewer (XR_MARCH_STEPS)
  const float PI         = 3.14159265358979;
  const float B_CRIT     = 2.59807621135;   // 3√3/2

  // Per-pixel jitter only — the heavy FBM evaluations are baked into
  // uNoiseTex (see buildFbmTexture); each fetch replaces a full 4–6
  // octave analytic FBM that used to dominate the frame cost.
  float hash21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

  const float NOISE_PERIOD_INV = 1.0 / 16.0;   // must match the bake

  // R = 4-octave FBM, G = 6-octave FBM, both in [0,1).
  vec2 fbmTex(vec2 p) {
    return texture2D(uNoiseTex, p * NOISE_PERIOD_INV).rg;
  }

  vec3 sampleSky(vec3 dir) {
    vec3 d = normalize(dir);
    if (uHasSkybox > 0.5) {
      vec3 raw = textureCube(uSkybox, d).rgb;
      // Boost bright pixels so lensed stars pile up visibly into the
      // arcs at the silhouette edge — exactly the photograph-like
      // behaviour real BH lensing produces.
      float lum = dot(raw, vec3(0.2126, 0.7152, 0.0722));
      float starBoost = smoothstep(0.12, 0.55, lum);
      return raw * (0.6 + starBoost * 4.5);
    }
    // Fallback if the skybox hasn't loaded yet: very faint star dust.
    return vec3(0.012, 0.012, 0.02);
  }

  // Octahedral unit-vector encoding (the reduced march's ray direction).
  vec2 octEncode(vec3 n) {
    n /= abs(n.x) + abs(n.y) + abs(n.z);
    vec2 sgn = vec2(n.x >= 0.0 ? 1.0 : -1.0, n.y >= 0.0 ? 1.0 : -1.0);
    return n.z >= 0.0 ? n.xy : (1.0 - abs(n.yx)) * sgn;
  }
  vec3 octDecode(vec2 e) {
    vec3 n = vec3(e, 1.0 - abs(e.x) - abs(e.y));
    vec2 sgn = vec2(n.x >= 0.0 ? 1.0 : -1.0, n.y >= 0.0 ? 1.0 : -1.0);
    if (n.z < 0.0) n.xy = (1.0 - abs(n.yx)) * sgn;
    return normalize(n);
  }

  #ifdef GARG_COMPOSITE
  uniform sampler2D uMarch;      // packed emission, transmittance, ray direction
  uniform vec2  uMarchSize;      // its size in texels
  uniform vec2  uFullSize;       // the target the composite draws into
  const float RING_BAND = 0.25;  // r_s of impact parameter past B_CRIT

  // One march texel, weighted: emission and transmittance into e, the
  // ray's direction into d. Shadow texels weigh nothing.
  void marchTap(vec2 texel, float w, bool inside, inout vec4 e, inout vec3 d, inout float wsum) {
    vec2 c = clamp(texel, vec2(0.0), uMarchSize - 1.0);
    vec4 t = texture2D(uMarch, (c + 0.5) / uMarchSize);
    // Only texels on the pixel's own side of the shadow's edge.
    float keep = (t.b > 5.0) == inside ? w : 0.0;
    vec2 rg = unpackHalf2x16(floatBitsToUint(t.r));
    vec2 bt = unpackHalf2x16(floatBitsToUint(t.g));
    e += vec4(rg, bt) * keep;
    d += (inside ? vec3(0.0, 0.0, 1.0) : octDecode(t.ba)) * keep;
    wsum += keep;
  }

  float marchGather(vec2 p, bool inside, out vec4 e, out vec3 d) {
    vec2 base = floor(p);
    vec2 f = p - base;
    float wsum = 0.0;
    e = vec4(0.0);
    d = vec3(0.0);
    marchTap(base, (1.0 - f.x) * (1.0 - f.y), inside, e, d, wsum);
    marchTap(base + vec2(1.0, 0.0), f.x * (1.0 - f.y), inside, e, d, wsum);
    marchTap(base + vec2(0.0, 1.0), (1.0 - f.x) * f.y, inside, e, d, wsum);
    marchTap(base + vec2(1.0, 1.0), f.x * f.y, inside, e, d, wsum);
    return wsum;
  }

  // The full-resolution pixel from the march: interpolated emission,
  // transmittance and ray direction, then the sky, the photon-ring lift
  // and the alpha exactly as the march's own ending computes them.
  // Inside the shadow it is the disc's emission in front of the hole over
  // opaque black; outside, the full composition.
  vec4 upsampleMarch(vec2 gradB, float b) {
    bool inside = b < B_CRIT;
    vec2 p = gl_FragCoord.xy * uMarchSize / uFullSize - 0.5;
    vec4 e;
    vec3 d;
    float wsum = marchGather(p, inside, e, d);
    if (wsum < 1e-4) {
      // A sliver on one side with all four texels on the other: step one
      // texel further onto the pixel's own side, along b's gradient.
      vec2 outward = length(gradB) > 1e-12 ? normalize(gradB) : vec2(0.0, 1.0);
      wsum = marchGather(p + (inside ? -outward : outward), inside, e, d);
    }
    if (wsum < 1e-4) return inside ? vec4(0.0, 0.0, 0.0, uOpacity) : vec4(0.0);
    e /= wsum;
    if (inside) return vec4(e.rgb * uOpacity, uOpacity);
    float trans = e.a;
    float ringDist = max(b - B_CRIT, 0.001);
    float photonRing = 1.0 + 2.4 * exp(-ringDist * 5.5);
    vec3 col = e.rgb + sampleSky(d) * trans * photonRing;
    float alpha = max(1.0 - trans, 1.0 - smoothstep(10.0, 55.0, b)) * uOpacity;
    return vec4(col * alpha, alpha);
  }
  #endif


  // Volumetric accretion-disk sample at a 3D point (r_s units, disk frame).
  // Returns (emission RGB, opacity-per-unit-length).
  //
  // Two-layer profile: a thin bright slab (the disk proper) plus a
  // broader, dimmer halo (volumetric dust extending above and below
  // the plane). The halo is what gives the Interstellar image its
  // soft glowing atmosphere around the bright disk core.
  vec4 diskSample(vec3 p, vec3 vel) {
    float r = length(p.xz);
    if (r < uDiskInner - 1.2 || r > uDiskOuter + 1.5) return vec4(0.0);

    float H     = 0.035 + 0.018 * r;
    float Hhalo = 0.30  + 0.10  * r;
    float vert  = exp(- (p.y * p.y) / (H     * H    ));
    float halo  = exp(- (p.y * p.y) / (Hhalo * Hhalo)) * 0.25;
    if (vert + halo < 0.005) return vec4(0.0);

    // Softer edges than the previous build: ISCO material bleeds
    // slightly inside diskInner so the hot inner ring still emits as
    // it accelerates inward; the outer edge fades into dust rather
    // than terminating in a hard cutoff.
    float rN = clamp((r - uDiskInner) / (uDiskOuter - uDiskInner), 0.0, 1.0);
    float edgeIn  = smoothstep(uDiskInner - 0.5, uDiskInner + 0.5, r);
    float edgeOut = 1.0 - smoothstep(uDiskOuter - 2.0, uDiskOuter + 1.0, r);
    float radial  = pow(1.0 - rN, 1.2) * edgeIn * edgeOut;
    if (radial < 0.001) return vec4(0.0);

    // Differential Keplerian rotation in a co-rotating cartesian frame.
    // Pure rotation — no radial inflow factor — because per-radius
    // scaling shears the noise pattern outward and reads as a visible
    // ripple instead of orbital flow.
    float omega = 2.2 / pow(r, 1.5);
    float rotAng = uTime * omega * 2.4;
    float cs = cos(rotAng), sn = sin(rotAng);
    vec2 q = vec2(cs * p.x - sn * p.z, sn * p.x + cs * p.z);

    // Multi-scale fractal: large filaments compound with medium
    // turbulence, fine detail, and a ridged streak overlay that
    // gives the visible plasma veins running with the orbital flow.
    // All six scales read from the baked FBM texture — same fractal
    // statistics, one fetch instead of ~112 sin-hash calls each.
    float n1     = fbmTex(q * 0.55).y;
    float n2     = fbmTex(q * 1.55).y;
    float detail = fbmTex(q * 5.5).x;
    float fine   = fbmTex(q * 12.0).x;
    float lane   = fbmTex(q * 3.4).x;
    float veins  = 1.0 - abs(2.0 * fbmTex(q * 2.2).x - 1.0);

    // Fractal layers compound multiplicatively so high-frequency
    // detail rides on top of the larger turbulence — true fractal
    // appearance rather than additive bands.
    float clumps = (0.55 + 0.95 * n1 + 0.50 * n2)
                 * (0.75 + 0.45 * detail)
                 * (0.85 + 0.30 * fine)
                 * mix(0.85, 1.15, veins);
    float laneMod = 0.55 + 0.45 * lane;
    float diskDensity = clumps * laneMod * radial * vert;
    float hazeDensity = (0.6 + 0.4 * n1) * radial * halo;

    // Four-stop palette: white-hot inner edge → gold → warm orange →
    // deep red outer. The white-hot stop is what gives the ISCO ring
    // its incandescent read against the silhouette.
    vec3 white = vec3(1.85, 1.70, 1.45);
    vec3 gold  = vec3(1.55, 1.10, 0.55);
    vec3 warm  = vec3(1.30, 0.55, 0.18);
    vec3 deep  = vec3(0.55, 0.18, 0.06);
    vec3 baseC = mix(white, gold, smoothstep(0.0,  0.22, rN));
    baseC      = mix(baseC, warm, smoothstep(0.22, 0.55, rN));
    baseC      = mix(baseC, deep, smoothstep(0.55, 1.00, rN));

    // View-independent brilliant ISCO ring — the white-hot halo
    // around the horizon, visible from any viewing angle. Built from
    // radial position alone so it doesn't disappear when the Doppler
    // beaming would otherwise zero one side.
    float ringR = smoothstep(uDiskInner + 2.0, uDiskInner - 0.2, r);
    float ringIntensity = pow(ringR, 1.8);
    vec3 ringColor = vec3(1.55, 1.40, 1.15);

    // Gentle Doppler — adds asymmetry without dominating. The earlier
    // 2.6-power version washed one side into pure blue, which fights
    // the warm disk read; this version stays subtle.
    vec3 tangent = normalize(vec3(-p.z, 0.0, p.x));
    float vOrb = min(0.55, 0.95 / sqrt(max(r, 1.2)));
    float beta = clamp(dot(tangent * vOrb, normalize(-vel)), -0.9, 0.9);
    float doppler = clamp(1.0 / (1.0 - beta), 0.7, 1.7);
    float gravRed = sqrt(max(0.0, 1.0 - RS / max(r, RS + 0.01)));

    // Final emission stack:
    //   1) thin disk's filamentary plasma (modulated by Doppler)
    //   2) view-independent ISCO ring (bright everywhere near inner edge)
    //   3) volumetric dust haze surrounding the disk
    vec3 emit = baseC * diskDensity * pow(doppler, 1.1) * gravRed * uDustEmission;
    emit += ringColor * ringIntensity * (vert * (0.6 + 0.4 * clumps) + halo * 0.4) * gravRed;
    emit += vec3(0.55, 0.30, 0.14) * hazeDensity * (0.45 + 0.55 * n2);

    // Thin slab fully occludes, haze is semi-transparent.
    float sigma = (diskDensity + hazeDensity * 0.4) * uDustOpacity;
    return vec4(emit, sigma);
  }

  void main() {
    // Camera and view in r_s-scaled BH-local space. Mesh transforms in
    // world space; we keep the raymarch in r_s units so the geodesic
    // step (which assumes r_s=1) stays correct regardless of scene
    // scale.
    vec3 viewDirW = normalize(vWorldPos - cameraPosition);
    vec3 viewDir  = viewDirW;                  // unit vectors don't scale
    vec3 camLocal = (cameraPosition - uBhWorldPos) / uRsWorld;

    // Per-pixel deterministic jitter. Phase-shifts adjacent rays so
    // the discrete disk-slab sub-samples don't all hit or all miss
    // the gaussian peak together. Spatial-only → no temporal flicker.
    float pxJit = hash21(gl_FragCoord.xy);

    vec3 pos = camLocal + viewDir * pxJit * 0.5;
    vec3 vel = viewDir;

    // Conserved angular momentum + impact parameter (Schwarzschild).
    vec3 L = cross(pos, vel);
    float h2 = dot(L, L);
    float b = sqrt(h2);
    bool inShadow = b < B_CRIT;

    // Far rays: barely deflected, no disk interaction — discard so the
    // underlying scene shows through cleanly. Tighter cut than the
    // complexthree.js shader because we don't render the background
    // here; we let the scene's own skybox do it.
    #ifdef GARG_COMPOSITE
    vec2 gradB = vec2(dFdx(b), dFdy(b));
    if (inShadow) {
      gl_FragColor = upsampleMarch(gradB, b);
      return;
    }
    if (b > 60.0) discard;
    float bandMix = smoothstep(B_CRIT + RING_BAND * 0.7, B_CRIT + RING_BAND, b);
    vec4 upsampled = vec4(0.0);
    if (bandMix > 0.0) {
      upsampled = upsampleMarch(gradB, b);
      if (bandMix >= 1.0) {
        gl_FragColor = upsampled;
        return;
      }
    }
    #endif
    if (!inShadow && b > 60.0) discard;

    float camR = length(camLocal);
    vec3 camDirL = camLocal / camR;
    float ESCAPE_R = max(60.0, camR * 1.6);
    float ESC2 = ESCAPE_R * ESCAPE_R;

    // Far-field step acceleration kicks in safely beyond any possible
    // disk interaction (the slab needs r < diskOuter + ~2; we leave an
    // 8 r_s margin). Bending falls off as 1/r⁴ out there, so growing
    // the step with r is visually lossless — and it's what lets rays
    // actually reach the escape radius instead of exhausting all
    // MAX_STEPS at the fixed 0.9 cap (the hero framing sits ~100 r_s
    // out, which needed ~280 capped steps for a full traversal).
    float farStart = uDiskOuter + 8.0;

    // Adaptive disk sub-sampling: 8 stratified samples at hero
    // distances; when the camera is far enough that the disk spans
    // few pixels, 4 are indistinguishable under the jitter.
    #ifndef GARG_SUBS
    #define GARG_SUBS 8
    #endif
    int   subs  = camR > 240.0 ? GARG_SUBS / 2 : GARG_SUBS;
    float subsF = float(subs);

    vec3 accumulated = vec3(0.0);
    float trans = 1.0;

    for (int i = 0; i < MAX_STEPS; i++) {
      if (float(i) >= uStepLimit) break;
      float r2 = dot(pos, pos);
      float r  = sqrt(r2);
      float dt = clamp(0.04 + r * 0.18, 0.04, 0.9)
               + max(0.0, r - farStart) * 0.22;

      vec3 acc = -1.5 * h2 * pos / (r2 * r2 * r);
      vel += acc * dt;
      vec3 newPos = pos + vel * dt;

      float r_xz_a = length(pos.xz);
      float r_xz_b = length(newPos.xz);
      bool radialBand = max(r_xz_a, r_xz_b) > uDiskInner - 1.2 &&
                        min(r_xz_a, r_xz_b) < uDiskOuter + 1.5;
      bool ySlab = (abs(pos.y) < 0.45) || (abs(newPos.y) < 0.45) ||
                   (sign(pos.y) != sign(newPos.y));

      if (radialBand && ySlab) {
        // Stratified sub-sampling — per-pixel + per-step jitter
        // breaks the staircase that fixed offsets produced where
        // adjacent rays landed at different y inside the slab.
        float subDt = dt / subsF;
        float stepJit = hash21(gl_FragCoord.xy + vec2(float(i) * 1.7, 0.0));
        for (int j = 0; j < GARG_SUBS; j++) {
          if (j >= subs) break;
          float t = (float(j) + stepJit) / subsF;
          vec3 sp = mix(pos, newPos, t);
          vec4 ds = diskSample(sp, vel);
          // A captured ray sees the disc between the camera and the hole
          // (the near side passing in front of the shadow), not what lies
          // behind it: near b_crit this march's coarse integrator lets some
          // rays the analytic test captures wind round and cross the far
          // disc, which drew a speckled ring just inside the shadow.
          if (inShadow) ds *= smoothstep(0.0, 1.0, dot(sp, camDirL));
          if (ds.w > 0.0) {
            float a = 1.0 - exp(-ds.w * subDt);
            accumulated += ds.rgb * subDt * trans;
            trans *= (1.0 - a);
            if (trans < 0.005) break;
          }
        }
      }

      pos = newPos;
      if (dot(pos, pos) < RS * RS) { break; }
      if (dot(pos, pos) > ESC2)    { break; }
      if (trans < 0.005)           { break; }
    }

    // Final lensed sky direction = ray velocity at escape, projected
    // back to world space. This is the key step that lets the existing
    // Milky Way panorama show up bent around the silhouette.
    vec3 lensedSky = vec3(0.0);
    if (!inShadow) {
      lensedSky = sampleSky(normalize(vel));
    }

    // Photon ring brightening — Bardeen showed each successive winding
    // contributes exp(-π) in width, so the narrow exponential profile
    // captures the dominant first-order ring at b ≈ b_crit.
    float ringDist = max(b - B_CRIT, 0.001);
    float photonRing = 1.0 + 2.4 * exp(-ringDist * 5.5);

    vec3 col = accumulated * uExposure;
    col += lensedSky * trans * photonRing;

    // Alpha: opaque inside the shadow, opaque where disk emission
    // accumulated, fading to scene-passthrough as the ray gets far
    // enough that there's nothing meaningful left to compose.
    // Inside the shadow the ray falls into the hole, so no sky reaches it,
    // but the disc it crossed on the way in (the near side passing in front
    // of the shadow) still does: col is that emission alone (lensedSky is
    // zero there) over an opaque black hole.
    float alpha;
    if (inShadow) {
      alpha = 1.0;
    } else {
      float diskAlpha = 1.0 - trans;
      float lensAlpha = 1.0 - smoothstep(10.0, 55.0, b);
      alpha = max(diskAlpha, lensAlpha);
    }

    gl_FragColor = vec4(col, alpha * uOpacity);
    #ifdef GARG_REDUCED
    // The reduced march hands the composite what the ray found rather than
    // a colour: the disc's emission and transmittance (half floats, two to
    // a channel) and the escaped ray's direction (octahedral, full float),
    // so the composite samples the sky per full-resolution pixel and lensed
    // stars stay a pixel wide. A high half is kept above 1e-5 so the packed
    // float is never a denormal a GPU could flush. B > 5 marks the shadow.
    vec3 emitR = accumulated * uExposure;
    gl_FragColor = vec4(
      uintBitsToFloat(packHalf2x16(vec2(emitR.r, max(emitR.g, 1e-5)))),
      uintBitsToFloat(packHalf2x16(vec2(emitR.b, max(trans, 1e-5)))),
      octEncode(normalize(vel)));
    // Shadow texels keep the emission picked up in front of the hole.
    if (inShadow) gl_FragColor.ba = vec2(10.0, 0.0);
    #endif
    #ifdef GARG_COMPOSITE
    gl_FragColor = mix(vec4(gl_FragColor.rgb * gl_FragColor.a, gl_FragColor.a), upsampled, bandMix);
    #endif
  }
`;

/**
 * Build the Gargantua group. Returns the same shape as src/blackhole.js
 * (object + tick + dispose + setOpacity) so the system builder can drop
 * it into the scene graph uniformly.
 *
 * @param {Object} opts
 * @param {number} [opts.rsWorld=6_000] Scene units per Schwarzschild radius.
 * @param {number} [opts.diskInner=2.6]
 * @param {number} [opts.diskOuter=12.0]
 * @param {number} [opts.exposure=1.0]
 * @param {() => THREE.Texture|null} [opts.getSkyboxTexture] Lazy accessor
 *   so the lensing picks up the panorama once it finishes loading.
 * @param {THREE.WebGLRenderer} [opts.renderer]
 */
export function createGargantua(opts = {}) {
  const rsWorld = opts.rsWorld || 6_000;
  const diskInner = opts.diskInner ?? 2.6;
  const diskOuter = opts.diskOuter ?? 12.0;

  const group = new THREE.Group();
  group.name = 'gargantua';

  // Sphere big enough that the player can sit anywhere reasonable
  // inside it and still get the BackSide raymarch over every screen
  // pixel. The 500k floor used to be tight: a 250k standoff from the
  // wormhole mouth (which is itself offset 720k from the BH) parked
  // the camera ~1.1M from the BH — outside the sphere, so the back
  // faces weren't visible and the whole shader went black. Bumped
  // to 10M so the camera can frame the disk from a wide range of
  // viewing distances without falling outside the lensing dome.
  const sphereRadius = Math.max(rsWorld * 1500, 10_000_000);

  const placeholderTex = new THREE.DataTexture(
    new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat
  );
  placeholderTex.needsUpdate = true;

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uBhWorldPos:   { value: new THREE.Vector3() },
      uRsWorld:      { value: rsWorld },
      uTime:         { value: 0 },
      uDiskInner:    { value: diskInner },
      uDiskOuter:    { value: diskOuter },
      uExposure:     { value: opts.exposure ?? 1.0 },
      uDustOpacity:  { value: 3.2 },
      uDustEmission: { value: 1.10 },
      uOpacity:      { value: 1.0 },
      uStepLimit:    { value: MARCH_STEPS_2D },
      uSkybox:       { value: opts.skyboxTexture || placeholderSkyCube() },
      uHasSkybox:    { value: opts.skyboxTexture ? 1 : 0 },
      // Module-level cached texture, shared across instances — not
      // disposed with the material.
      uNoiseTex:     { value: buildFbmTexture() },
    },
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    transparent: true,
    blending: THREE.NormalBlending,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: true,
    toneMapped: true,
  });

  const geometry = new THREE.SphereGeometry(sphereRadius, 64, 32);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'gargantuaRaymarcher';
  const reduced = createReducedMarch(mesh, material, geometry);
  let xrOn = false;
  mesh.renderOrder = 100;
  // Frustum culling can wrongly cull a huge BackSide sphere when the
  // camera is inside it; disable it so the raymarcher always runs.
  mesh.frustumCulled = false;
  group.add(mesh);

  // Faint accretion glow as a point light so any orbiting planets get
  // a hint of warm illumination from the disk side. Not physically
  // accurate (the disk isn't a point) but visually right enough that
  // Miller/Mann/Edmunds aren't pitch black on the BH side.
  const light = new THREE.PointLight(0xff9858, 1.8, rsWorld * 800, 1.5);
  group.add(light);

  group.userData = {
    isBlackHole: true,
    isGargantua: true,
    name: 'Gargantua',
    type: 'Supermassive Black Hole',
    description:
      `Supermassive black hole, ~${GARGANTUA_CONSTANTS.massSolar.toExponential(1)} solar masses. ` +
      `Near-extremal Kerr spin a/M = ${GARGANTUA_CONSTANTS.spinParameter}. ` +
      `Visualized with its accretion disk under Schwarzschild light deflection.`,
    radius: rsWorld * 12,
  };

  const handle = {
    object: group,
    options: { rsWorld, diskInner, diskOuter },
    constants: GARGANTUA_CONSTANTS,

    setOpacity(value) {
      const v = Math.max(0, Math.min(1, value));
      material.uniforms.uOpacity.value = v;
      mesh.visible = v > 0.001;
    },

    // Raymarch resolution as a share of the target's (1 = full, today's
    // path). See "Reduced-resolution march" above the fragment shader.
    setMarchScale(scale) {
      reduced.setScale(scale);
    },

    // Pick the march resolution from a pixel budget instead (marching at
    // most `pixels` per frame, never below minScale of the target), and
    // with adapt, step it down while frames miss 60 Hz.
    setMarchBudget(pixels, { adapt = true, minScale = 0.25 } = {}) {
      reduced.setBudget(pixels, adapt, minScale);
    },

    // The frame time the page is paced to, read each time the pacing is
    // judged (a function, so a cap that changes is followed): the live
    // wallpaper draws at most 30 fps, and against 60 Hz its every frame
    // looked slow, so the march stepped down to minScale on any GPU.
    setFrameTarget(getMs) {
      reduced.setFrameTarget(getMs);
    },

    get marchScale() { return reduced.effectiveScale(); },
    get reducedMaterial() { return reduced.material; },

    tick(time) {
      // In a headset: the full-resolution march in the eye's own pass (the
      // reduced march renders into a target by gl_FragCoord, which an XR
      // layer's two viewports do not match) with fewer steps.
      const xr = !!(opts.renderer && opts.renderer.xr && opts.renderer.xr.isPresenting);
      if (xr !== xrOn) {
        xrOn = xr;
        reduced.setXr(xr);
        material.uniforms.uStepLimit.value = xr ? XR_MARCH_STEPS : MARCH_STEPS_2D;
      }
      reduced.noteFrame();
      material.uniforms.uTime.value = time;
      group.getWorldPosition(material.uniforms.uBhWorldPos.value);

      if (opts.getSkyboxTexture) {
        const tex = opts.getSkyboxTexture();
        if (tex && material.uniforms.uSkybox.value !== tex) {
          material.uniforms.uSkybox.value = tex;
          material.uniforms.uHasSkybox.value = 1;
          if (opts.renderer && tex.anisotropy < 4) {
            tex.anisotropy = opts.renderer.capabilities.getMaxAnisotropy();
            tex.needsUpdate = true;
          }
        }
      }
    },

    dispose() {
      reduced.dispose();
      geometry.dispose();
      material.dispose();
    },
  };
  // Reachable from the system's body group (handle.bodies.<id>.raymarch),
  // which is all the scene code holds. Not enumerable, so userData copies
  // and serialisation leave it alone.
  Object.defineProperty(group, 'raymarch', { value: handle });
  return handle;
}

// The reduced-resolution march (see its comment above the fragment
// shader): the raymarch material
// renders into its own target from the composite mesh's onBeforeRender, the
// nested-render pattern three's Reflector uses, so it always marches for
// the camera and target that are drawing the mesh this frame.
// Disc sub-samples per slab step in the reduced march (the full march takes
// 8). Measured against grain and cost; see the march's comment.
const REDUCED_SUBS = 12;

function createReducedMarch(mesh, marchMaterial, geometry) {
  const u = marchMaterial.uniforms;
  u.uMarch = { value: null };
  u.uMarchSize = { value: new THREE.Vector2(1, 1) };
  u.uFullSize = { value: new THREE.Vector2(1, 1) };
  // The full-resolution draw: the same shader, compositing the march and
  // marching only the band round the shadow itself. Premultiplied output.
  const composite = new THREE.ShaderMaterial({
    uniforms: u,
    defines: { GARG_COMPOSITE: '' },
    vertexShader: marchMaterial.vertexShader,
    fragmentShader: marchMaterial.fragmentShader,
    transparent: true,
    premultipliedAlpha: true,
    blending: THREE.NormalBlending,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: true,
    toneMapped: true,
    extensions: { derivatives: true },
  });
  // Same uniforms object as the full-resolution material, so tick() and
  // setOpacity() drive both.
  const reducedMaterial = new THREE.ShaderMaterial({
    uniforms: u,
    // Twice the disc sub-samples of the full-resolution march: at a fifth
    // of the pixels it can afford them, and it keeps the jitter's grain as
    // fine as the full-resolution march's once upscaled.
    defines: { GARG_REDUCED: '', GARG_SUBS: REDUCED_SUBS },
    vertexShader: marchMaterial.vertexShader,
    fragmentShader: marchMaterial.fragmentShader,
    blending: THREE.NoBlending,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: false,
    toneMapped: false,
  });
  const marchScene = new THREE.Scene();
  const marchMesh = new THREE.Mesh(geometry, reducedMaterial);
  marchMesh.matrixAutoUpdate = false;
  marchMesh.frustumCulled = false;
  marchScene.add(marchMesh);

  let fixedScale = 1;
  let budget = 0, adapt = false, minScale = 0.25, adaptFactor = 1;
  let target = null;
  let lastScale = 1;
  const full = new THREE.Vector2();
  const clear = new THREE.Color();
  const frames = [];
  let lastFrame = null, firstFrame = null, cooldownUntil = 0, slowStrikes = 0;
  let frameTargetOf = null;

  function scaleFor(w, h) {
    if (!budget) return fixedScale;
    const s = Math.sqrt(budget / Math.max(1, w * h)) * adaptFactor;
    return THREE.MathUtils.clamp(s, minScale, 1);
  }

  let xr = false;
  function apply() {
    const reducedOn = budget > 0 || fixedScale < 0.999;
    mesh.material = reducedOn && !xr ? composite : marchMaterial;
  }

  mesh.onBeforeRender = (renderer, scene, camera) => {
    if (mesh.material !== composite) return;
    const prevTarget = renderer.getRenderTarget();
    if (prevTarget) full.set(prevTarget.width, prevTarget.height);
    else renderer.getDrawingBufferSize(full);
    const scale = scaleFor(full.x, full.y);
    lastScale = scale;
    const w = Math.max(1, Math.round(full.x * scale));
    const h = Math.max(1, Math.round(full.y * scale));
    if (!target) {
      target = new THREE.WebGLRenderTarget(w, h, {
        type: THREE.FloatType,
        depthBuffer: false,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        generateMipmaps: false,
      });
    } else if (target.width !== w || target.height !== h) {
      target.setSize(w, h);
    }
    marchMesh.matrix.copy(mesh.matrixWorld);
    marchMesh.matrixWorld.copy(mesh.matrixWorld);

    const prevAutoClear = renderer.autoClear;
    const prevAlpha = renderer.getClearAlpha();
    renderer.getClearColor(clear);
    renderer.setRenderTarget(target);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, false, false);
    renderer.autoClear = false;
    renderer.render(marchScene, camera);
    renderer.autoClear = prevAutoClear;
    renderer.setClearColor(clear, prevAlpha);
    renderer.setRenderTarget(prevTarget);

    u.uMarch.value = target.texture;
    u.uMarchSize.value.set(w, h);
    u.uFullSize.value.copy(full);
  };

  return {
    setXr(on) {
      xr = !!on;
      apply();
    },
    setScale(scale) {
      fixedScale = THREE.MathUtils.clamp(Number(scale) || 1, 0.1, 1);
      budget = 0;
      adapt = false;
      apply();
    },
    setBudget(pixels, adaptOn, floor) {
      budget = Math.max(0, Number(pixels) || 0);
      adapt = !!adaptOn;
      minScale = THREE.MathUtils.clamp(floor || 0.25, 0.1, 1);
      adaptFactor = 1;
      frames.length = 0;
      apply();
    },
    setFrameTarget(getMs) {
      frameTargetOf = typeof getMs === 'function' ? getMs : null;
      frames.length = 0;
    },
    // For the capture scripts: the reduced march's own material.
    material: reducedMaterial,
    effectiveScale() {
      return mesh.material === composite ? lastScale : 1;
    },
    // Frame pacing for the budget. After a 6 s warm-up (loading and shader
    // compiles are slow frames that say nothing about the GPU), when the
    // median frame misses its target (60 Hz, or the page's frame cap, from
    // setFrameTarget) in two 120-frame windows running the march drops to
    // 0.8 of its resolution, at most every 3 s and never below minScale;
    // once it runs well under (two thirds of the target, 11 ms at 60 Hz) it
    // climbs back by 1.15, at most every 8 s, up to the budget. The gap
    // between the two keeps it from pumping. Under a cap the frames never
    // come in under it, so a capped page only steps down.
    noteFrame() {
      const now = performance.now();
      if (firstFrame === null) firstFrame = now;
      if (lastFrame !== null) {
        const dt = now - lastFrame;
        if (dt < 250) frames.push(dt);
        if (frames.length > 120) frames.shift();
      }
      lastFrame = now;
      if (!adapt || !budget || now - firstFrame < 6000) return;
      if (frames.length < 120 || now < cooldownUntil) return;
      const sorted = frames.slice().sort((a, b) => a - b);
      const median = sorted[sorted.length >> 1];
      const targetMs = Math.max(1000 / 60, (frameTargetOf && frameTargetOf()) || 0);
      const slow = median > targetMs * 1.08 && lastScale > minScale + 1e-3;
      slowStrikes = slow ? slowStrikes + 1 : 0;
      frames.length = 0;
      if (slowStrikes >= 2) {
        adaptFactor *= 0.8;
        slowStrikes = 0;
        cooldownUntil = now + 3000;
      } else if (!slow && median < targetMs * 0.66 && adaptFactor < 1) {
        adaptFactor = Math.min(1, adaptFactor * 1.15);
        frames.length = 0;
        cooldownUntil = now + 8000;
      }
    },
    dispose() {
      if (target) target.dispose();
      composite.dispose();
      reducedMaterial.dispose();
    },
  };
}
